import { Delete, Edit, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  IconButton,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC } from 'react';
import { formatMoney, isAllowedToBeEditedOrDeleted } from '../../../../../helpers';
import { IUserJobTitle } from '../../../../../models';

interface IMobileManageJobTitles {
  original: IUserJobTitle;
  handleEdit?: (val: IUserJobTitle) => void;
  handleDelete?: (val: IUserJobTitle) => void;
  canEdit: boolean;
}

export const MobileManageJobTitles: FC<IMobileManageJobTitles> = ({
  original,
  handleEdit,
  handleDelete,
  canEdit
}) => {
  const classes = useStyles();
  const { displayName, isPlaceOrderRole, materialSpendLimit, changeOrderSpendLimit, canDelete } =
    original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">{displayName}</Typography>

          <div className={classes.buttonsWrapper}>
            {canEdit && isAllowedToBeEditedOrDeleted(displayName) && handleEdit && (
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleEdit(original);
                }}
                size="large"
              >
                <Edit />
              </IconButton>
            )}
            {canEdit && canDelete && isAllowedToBeEditedOrDeleted(displayName) && handleDelete && (
              <IconButton
                className={classes.deleteButton}
                color="default"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(original);
                }}
                size="large"
              >
                <Delete />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Job Title:
          </FormLabel>
          <Typography component="span" variant="body2">
            {displayName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Place Order Role:
          </FormLabel>
          <Typography component="span" variant="body2">
            {isPlaceOrderRole ? 'Yes' : 'No'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Materials Spend Limit:
          </FormLabel>
          <Typography component="span" variant="body2">
            {materialSpendLimit ? formatMoney(materialSpendLimit, 0) : '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Change Order Spend Limit:
          </FormLabel>
          <Typography component="span" variant="body2">
            {changeOrderSpendLimit ? formatMoney(changeOrderSpendLimit, 0) : '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  }
}));
